/**
 * Microsoft 365 Copilot calls our agents: a project's agents that Copilot Studio can call over
 * A2A, the ones that could be, and the tasks asked of them (never what was asked, only who, which
 * Copilot agent and how it went). Enterprise only.
 */

import type { A2aAgentsResponse, A2aCandidate, A2aTaskView, A2aWhoMayExpose } from '@aioe/shared';
import { Comments, Copy } from '@gravity-ui/icons';
import { useState } from 'react';

import { problemText, routes } from '../api/client.ts';
import { timeAgo, useLoad } from '../hooks.ts';
import { SubjectName } from './SubjectPicker.tsx';
import { Button, ChoiceSelect, ErrorNote, ListBlock, ListGroup, ListRow, PlainPanel, StatusMark, Toggle } from './ui.tsx';
import { ArtEmpty, VIEW_TONES } from './ui/Art.tsx';

const tone = VIEW_TONES.projects!;

const STATE: Record<A2aTaskView['state'], { state: 'working' | 'connected' | 'idle' | 'error'; label: string }> = {
  submitted: { state: 'working', label: 'Started' },
  working: { state: 'working', label: 'Working' },
  'input-required': { state: 'idle', label: 'Waiting for an answer' },
  completed: { state: 'connected', label: 'Done' },
  canceled: { state: 'idle', label: 'Stopped' },
  failed: { state: 'error', label: 'Failed' },
  rejected: { state: 'error', label: 'Refused' },
};

function CopyButton({ text, label }: { text: string; label: string }) {
  const [done, setDone] = useState(false);
  return (
    <Button size="sm" variant="ghost" aria-label={label} onPress={() => void navigator.clipboard?.writeText(text).then(() => (setDone(true), setTimeout(() => setDone(false), 1500)))}>
      {done ? 'Copied' : <Copy />}
    </Button>
  );
}

export function A2aAgentsPanel({ projectId }: { projectId: string }) {
  const agents = useLoad(() => routes.a2aAgents(projectId), [projectId]);
  const tasks = useLoad(() => routes.a2aTasks(projectId), [projectId], 30_000);
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState<string>();
  const d: A2aAgentsResponse | undefined = agents.data;
  if (!d) return <ErrorNote error={agents.error} />;
  if (!d.agents.length && !d.canExpose) return null;
  const act = async (key: string, run: () => Promise<unknown>) => {
    setBusy(key);
    setError(undefined);
    try {
      await run();
      agents.reload();
    } catch (e) {
      setError(problemText(e));
    } finally {
      setBusy(undefined);
    }
  };
  const expose = (c: A2aCandidate): void => void exposeNow(c);
  const exposeNow = (c: A2aCandidate) => act(`${c.ref}/${c.member ?? ''}`, () => routes.exposeA2aAgent(projectId, { kind: c.kind, ref: c.ref, ...(c.member ? { member: c.member } : {}), name: c.member ? `${c.member} (${c.from.replace(/^Member of /, '')})` : c.name, ...(c.description ? { description: c.description } : {}) }));
  const recent = tasks.data?.tasks ?? [];
  const groups = groupsOf(d);
  return (
    <>
      <ListGroup
        title="Microsoft 365 Copilot"
        count={d.agents.length || undefined}
        description={`Switch a team or specialist on and people can ask it for help from Microsoft 365 Copilot. The work runs on the asking person's own workbench, as them, under this project's policy. ${d.allowed ? 'Each one switched on gets an address to add in Copilot Studio.' : 'Calls are off until the policy allows them (Policies, Copilot agents this organisation talks to).'}`}
        empty={
          !d.agents.length && !(d.canExpose && d.candidates.length) && !error ? (
            <ArtEmpty tone={tone} icon={<Comments />} title="Nothing to offer Copilot yet" compact>
              When the project has a team or a specialist, an owner can make it callable from Microsoft 365 Copilot here.
            </ArtEmpty>
          ) : undefined
        }
      >
        {error ? (
          <ListBlock>
            <ErrorNote error={error} />
          </ListBlock>
        ) : null}
        {groups.map((g) => (
          <CallableRow key={g.ref} g={g} canExpose={d.canExpose} busy={busy} onExpose={expose} onStop={(id) => void act(id, () => routes.unexposeA2aAgent(projectId, id))} />
        ))}
        {d.agents.length ? (
          <ListBlock>
            <details className="text-xs text-muted">
              <summary className="cursor-pointer select-none">How to add a callable one in Copilot Studio</summary>
              <p className="mt-1 max-w-prose">
                In Copilot Studio: Agents, Add an agent, Connect to an external agent, Agent2Agent. Give the agent's address from here and an OAuth 2.0 connection to Microsoft Entra ID asking for{' '}
                <span className="mono">{d.scope}</span>. The steps are in the Microsoft setup guide.
              </p>
            </details>
          </ListBlock>
        ) : null}
      </ListGroup>
      {recent.length ? (
        <ListGroup title="Asked lately from Copilot" count={recent.length} description="Who asked which of them, and how it went. Never what was asked.">
          {recent.slice(0, 10).map((t) => (
            <ListRow
              key={t.id}
              title={t.name}
              description={
                <>
                  Asked by <SubjectName id={t.principal} /> {timeAgo(t.createdAt)}
                </>
              }
              status={<StatusMark state={STATE[t.state].state} label={t.refusal && t.state === 'rejected' ? `Refused: ${t.refusal}` : STATE[t.state].label} wrap />}
            />
          ))}
        </ListGroup>
      ) : null}
    </>
  );
}

type Exposed = A2aAgentsResponse['agents'][number];
/** One team or specialist with its own exposure, and for a team its agent members with theirs. */
interface CallableGroup {
  ref: string;
  name: string;
  kind: 'team' | 'specialist';
  description?: string;
  self?: { candidate?: A2aCandidate; exposed?: Exposed };
  members: Array<{ name: string; candidate?: A2aCandidate; exposed?: Exposed }>;
}

/** The candidates and the callable ones, by team: a team heads its members; a specialist is one entry. */
function groupsOf(d: A2aAgentsResponse): CallableGroup[] {
  const byRef = new Map<string, CallableGroup>();
  const group = (ref: string, name: string, kind: 'team' | 'specialist', description?: string) => {
    let g = byRef.get(ref);
    if (!g) byRef.set(ref, (g = { ref, name, kind, ...(description ? { description } : {}), members: [] }));
    return g;
  };
  for (const c of d.candidates) {
    if (c.member) group(c.ref, c.from.replace(/^Member of /, ''), 'team').members.push({ name: c.member, candidate: c });
    else group(c.ref, c.name, c.from === 'Specialist' ? 'specialist' : 'team', c.description).self = { candidate: c };
  }
  for (const a of d.agents) {
    if (a.member && a.kind === 'specialist') {
      const g = byRef.get(a.ref) ?? group(a.ref, a.ref, 'team');
      const m = g.members.find((x) => x.name === a.member);
      if (m) m.exposed = a;
      else g.members.push({ name: a.member, exposed: a });
    } else {
      const g = group(a.ref, a.name, a.kind === 'team' ? 'team' : 'specialist', a.description);
      g.self = { ...(g.self ?? {}), exposed: a };
    }
  }
  return [...byRef.values()].sort((a, b) => a.name.localeCompare(b.name));
}

/** A callable one's address, to copy into Copilot Studio. */
function Address({ a }: { a: Exposed }) {
  return (
    <span className="flex min-w-0 items-center gap-1 text-xs text-muted">
      <span className="mono truncate">{a.url}</span>
      <CopyButton text={a.url} label={`Copy ${a.name}'s address`} />
    </span>
  );
}

/** One team or specialist: what it is, one switch for it, and (a team) its members one by one on demand. */
function CallableRow({ g, canExpose, busy, onExpose, onStop }: { g: CallableGroup; canExpose: boolean; busy?: string; onExpose: (c: A2aCandidate) => void; onStop: (id: string) => void }) {
  const membersOn = g.members.filter((m) => m.exposed).length;
  const [open, setOpen] = useState(membersOn > 0);
  const on = Boolean(g.self?.exposed);
  const key = `${g.ref}/`;
  const flip = (next: boolean, c?: A2aCandidate, e?: Exposed) => (next ? c && onExpose(c) : e && onStop(e.id));
  // A specialist is one agent; a team that lists no agent members reads as one too (its member is the specialist itself).
  const specialist = g.kind === 'specialist' || !g.members.length;
  const summary = specialist ? 'Specialist' : `Team of ${g.members.length} ${g.members.length === 1 ? 'agent' : 'agents'}: ${g.members.map((m) => m.name).join(', ')}`;
  const control = canExpose ? (
    <>
      {busy !== undefined && (busy === key || busy === g.self?.exposed?.id) ? <span className="text-xs text-muted">Saving…</span> : null}
      <Toggle checked={on} label={`${g.name} callable from Copilot`} isDisabled={!g.self?.candidate && !g.self?.exposed} onChange={(v) => flip(v, g.self?.candidate, g.self?.exposed)} />
    </>
  ) : undefined;
  return (
    <ListRow
      title={g.name}
      description={
        <>
          <span className="block">{g.description ?? summary}</span>
          {g.description ? <span className="block">{summary}</span> : null}
        </>
      }
      {...(control ? { control } : { status: <StatusMark state={on ? 'online' : 'idle'} label={on ? 'Callable' : 'Not callable'} /> })}
    >
      {g.self?.exposed || (g.kind === 'team' && g.members.length) ? (
        <div className="grid gap-1">
          {g.self?.exposed ? <Address a={g.self.exposed} /> : null}
          {g.kind === 'team' && g.members.length ? (
            <>
              <button type="button" className="justify-self-start text-xs text-accent hover:underline" onClick={() => setOpen((o) => !o)}>
                {open ? 'Hide members' : membersOn ? `${membersOn} of ${g.members.length} members callable on their own` : 'Make members callable on their own'}
              </button>
              {open ? (
                <ul className="m-0 grid list-none gap-1.5 border-s border-separator p-0 ps-3 text-sm">
                  {g.members.map((m) => (
                    <li key={m.name} className="flex items-center justify-between gap-4">
                      <span className="grid min-w-0 gap-0.5">
                        <span>{m.name}</span>
                        {m.exposed ? <Address a={m.exposed} /> : null}
                      </span>
                      {canExpose ? (
                        <Toggle checked={Boolean(m.exposed)} label={`${m.name} callable on their own`} isDisabled={busy !== undefined && (busy === `${g.ref}/${m.name}` || busy === m.exposed?.id)} onChange={(v) => flip(v, m.candidate, m.exposed)} />
                      ) : (
                        <StatusMark state={m.exposed ? 'online' : 'idle'} label={m.exposed ? 'Callable' : 'Not callable'} />
                      )}
                    </li>
                  ))}
                </ul>
              ) : null}
            </>
          ) : null}
        </div>
      ) : null}
    </ListRow>
  );
}

/** Settings: who may make agents callable from Copilot. */
export function A2aSettingsPanel() {
  const settings = useLoad(() => routes.a2aSettings(), []);
  const [error, setError] = useState<string>();
  const set = async (whoMayExpose: A2aWhoMayExpose) => {
    setError(undefined);
    try {
      await routes.setA2aSettings({ whoMayExpose });
      settings.reload();
    } catch (e) {
      setError(problemText(e));
    }
  };
  return (
    <PlainPanel title="Microsoft 365 Copilot">
      <ErrorNote error={error ?? settings.error} />
      <div className="flex flex-wrap items-center justify-between gap-3 text-sm">
        <div className="min-w-48 flex-1">
          <div>Who may make agents callable from Copilot</div>
          <div className="text-xs text-muted">Each project's specialists and teams are made callable on the project's page. Whether any call is allowed at all is the policy's say.</div>
        </div>
        <ChoiceSelect
          label="Who may make agents callable"
          value={settings.data?.whoMayExpose ?? 'adminsAndOwners'}
          options={{ adminsAndOwners: 'Administrators and project owners', admins: 'Administrators only' } as Record<A2aWhoMayExpose, string>}
          onChange={(v) => void set(v)}
        />
      </div>
    </PlainPanel>
  );
}
